import {
  Component,
  ContentChildren,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgpSelect,
  NgpSelectDropdown,
  NgpSelectOption,
  NgpSelectPortal,
  provideSelectState,
} from 'ng-primitives/select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faChevronDown,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import { SuiFormControlBase } from '@sensika/sui/shared';
import { resolveSuiBoundary } from '@sensika/sui/shared';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';
import { SuiSelectOptionComponent } from './sui-select-option.component';

@Component({
  selector: 'sui-select',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NgpSelect,
    NgpSelectPortal,
    NgpSelectDropdown,
    NgpSelectOption,
    FaIconComponent,
  ],
  providers: [
    provideSelectState(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiSelectComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiSelectComponent),
    },
  ],
  templateUrl: './sui-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-select.component.scss',
})
export class SuiSelectComponent
  extends SuiFormControlBase<unknown>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-select-${++SuiSelectComponent._idCounter}`;
  private _focused = false;

  protected readonly faChevronDown = faChevronDown;
  protected readonly faCircleExclamation = faCircleExclamation;

  readonly controlType = 'select';

  @Input() id = '';
  @Input() placeholder = 'Select...';

  /**
   * Container the dropdown portals into. Defaults to the nearest
   * `data-sui-popover-boundary` pane (else `body`), so in-dialog selects stack
   * correctly with no wiring - pass this only to override (e.g. `'body'` for a
   * pane that would clip the dropdown).
   */
  @Input() dropdownContainer?: string | HTMLElement;

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Resolved per CD (no caching) so a portaled/moved select stays correct. */
  protected get resolvedDropdownContainer(): string | HTMLElement {
    return (
      this.dropdownContainer ?? resolveSuiBoundary(this.hostEl.nativeElement)
    );
  }

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    const v = this.value();
    return v === null || v === undefined || v === '';
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  @ContentChildren(SuiSelectOptionComponent)
  contentOptions!: QueryList<SuiSelectOptionComponent>;

  protected get selectedLabel(): string {
    const val = this.value();
    const match = this.contentOptions?.find((o) => o.value === val);
    if (match?.label) return match.label;
    return this.empty ? this.placeholder : String(val);
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  protected onValueChange(newValue: unknown): void {
    this.setValue(newValue);
  }
}
